import { useState, useEffect, useRef } from "react";
import { CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";

export default function DateRangeFilter({ onDateChange }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const [selectedDateText, setSelectedDateText] = useState("Select date range");
  const calendarRef = useRef(null);
  const buttonRef = useRef(null);

  // Format date as "MMM DD, YYYY"
  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const applyDateRange = () => {
    if (startDate) {
      // Pass the date to parent
      onDateChange({
        startDate,
        endDate: endDate || startDate,
      });

      // Close the calendar
      setShowCalendar(false);
    }
  };

  // Update the button text when dates change
  useEffect(() => {
    if (startDate && endDate) {
      setSelectedDateText(`${formatDate(startDate)} - ${formatDate(endDate)}`);
    } else if (startDate) {
      setSelectedDateText(formatDate(startDate));
    } else {
      setSelectedDateText("Select date range");
    }
  }, [startDate, endDate]);

  // Generate days for the current month view
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  // Handle date selection
  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate) || date < startDate) {
      setStartDate(new Date(date));
      setEndDate(null);
    } else {
      setEndDate(new Date(date));
    }
  };

  // Check if a date is in the selected range
  const isInRange = (date) => {
    if (!startDate || !date) return false;

    if (!endDate && hoverDate) {
      const hoverTime = hoverDate.getTime();
      const startTime = startDate.getTime();
      const dateTime = date.getTime();
      return dateTime > startTime && dateTime <= hoverTime;
    }

    if (startDate && endDate) {
      const startTime = startDate.getTime();
      const endTime = endDate.getTime();
      const dateTime = date.getTime();
      return dateTime >= startTime && dateTime <= endTime;
    }

    return false;
  };

  // Check if a date is the start or end of the range
  const isRangeEnd = (date) => {
    if (!date || !startDate) return false;

    const dateTime = date.getTime();
    const startTime = startDate.getTime();
    const endTime = endDate ? endDate.getTime() : null;

    return dateTime === startTime || (endTime !== null && dateTime === endTime);
  };

  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Clear the selected date range
  const clearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  // Close the calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative">
      {/* Date selector button */}
      <button
        ref={buttonRef}
        onClick={() => setShowCalendar(!showCalendar)}
        className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
      >
        <CalendarIcon className="w-4 h-4 mr-2 text-gray-500" />
        <span>{selectedDateText}</span>
      </button>

      {/* Calendar popup */}
      {showCalendar && (
        <div
          ref={calendarRef}
          className="absolute mt-1 z-10 bg-white rounded-md shadow-lg p-4 border border-gray-200 w-80"
        >
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h3 className="text-sm font-medium text-gray-700">
              {currentMonth.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </h3>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Days of week header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
              marginBottom: "8px",
            }}
          >
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="text-xs font-medium text-gray-500 text-center w-[calc(100%/7)]"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
            }}
            onMouseLeave={() => setHoverDate(null)}
          >
            {getDaysInMonth().map((date, index) => (
              <div
                key={index}
                className={`
        h-8 w-[calc(100%/7)] flex items-center gap-4 justify-center text-sm
        ${!date ? "invisible" : "cursor-pointer"}
        ${date && isRangeEnd(date) ? "bg-red-600 text-white rounded-full" : ""}
        ${
          date && isInRange(date) && !isRangeEnd(date)
            ? "bg-red-100 text-gray-900"
            : ""
        }
        ${
          date && !isInRange(date) && !isRangeEnd(date)
            ? "hover:bg-gray-100 rounded-full"
            : ""
        }
      `}
                onClick={() => date && handleDateClick(date)}
                onMouseEnter={() =>
                  date && startDate && !endDate && setHoverDate(date)
                }
              >
                <div className="h-8 w-8 flex items-center justify-center rounded-full">
                  {date ? date.getDate() : ""}
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex justify-between">
            <button
              type="button"
              onClick={clearDateRange}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </button>
            <button
              type="button"
              onClick={applyDateRange}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              disabled={!startDate}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
